import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import apiRequest, { ApiResponse } from "../axios/useApi";
import { formatDate, handleApiError } from "../lib/utils";
import { UIContext } from "./UIProvider";
import { Filters } from "../components/Filter/Filter";

interface Invoice {
  invoiceId: string;
  businessMasterId: string;
  refInvoiceId: string;
  refInvoiceDate: string;
  refInvoiceDueDate: string;
  timeZoneId: number;
  customerId: string;
  subTotalAmt: number;
  discountAmt: number;
  totalDiscountAmt: number;
  roundOffAmt: number;
  totalTaxAmt: number;
  totalAmt: number;
  outstandingAmt: number;
  isCancelled: boolean;
  cancelledDateTime: string;
  invoiceStatusId: number;
  invoiceStatusText: string;
  customerName: string;
  createdDateTime: string;
  updatedDateTime: string;
}

interface InvoiceStatus {
  masterId: number;
  codeId: string;
  codeName: string;
}

interface InvoiceContextType {
  invoices: Invoice[];
  isLoading: boolean;
  handleApplyFilter: (filterValues: Filters) => void;
  handleClearFilter: () => void;
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  totalRecords: number;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
  customerOptions: { label: string; value: string }[];
}

export const InvoiceContext = createContext<InvoiceContextType>({
  invoices: [],
  isLoading: false,
  handleApplyFilter: () => {},
  handleClearFilter: () => {},
  pageSize: 10,
  pageNumber: 1,
  totalPages: 0,
  totalRecords: 0,
  handlePageChange: () => {},
  handlePageSizeChange: () => {},
  customerOptions: [],
});

type Props = PropsWithChildren;

export const InvoiceProvider = ({ children }: Props) => {
  const { onError, onSuccess, businessId } = useContext(UIContext);
  const [isLoading, setIsLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customerOptions, setCustomerOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [filterValues, setFilterValues] = useState<Filters>({});
  // Pagination state
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const handleApplyFilter = (filterValues: Filters) => {
    setFilterValues(filterValues);
    getInvoiceData(1, pageSize, filterValues);
  };

  const handleClearFilter = () => {
    setFilterValues({});
    getInvoiceData(1, pageSize, {});
  };

  const getInvoiceData = async (
    page: number,
    size: number,
    filterValues?: Filters
  ) => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        pageSize: size.toString(),
      });

      if (filterValues?.startDate) {
        queryParams.append("startDate", formatDate(filterValues.startDate));
      }
      if (filterValues?.endDate) {
        queryParams.append("endDate", formatDate(filterValues.endDate));
      }
      if (filterValues?.status) {
        queryParams.append("statusId", filterValues.status);
      }
      if (filterValues?.customerId) {
        queryParams.append("customerId", filterValues.customerId);
      }

      const response: ApiResponse<{
        totalPages: number;
        totalRecords: number;
        pageNumber: number;
        pageSize: number;
        items: Invoice[];
      }> = await apiRequest({
        url: `/receivableinvoice/${businessId}/List/${page}?${queryParams}`,
        method: "GET",
      });

      if (response.success) {
        const { totalPages, items, pageNumber, totalRecords } =
          response?.data || {};
        onSuccess?.(
          "GET",
          "getInvoices",
          response?.message || "Data retrieved successfully!"
        );
        setInvoices(items || []);
        setTotalPages(totalPages || 0);
        setPageNumber(pageNumber || 1);
        setTotalRecords(totalRecords || 0);
      } else {
        handleApiError(response, onError);
      }
    } catch (err) {
      onError?.(new Error("An error occurred while fetching Invoice data."));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getInvoiceData(pageNumber, pageSize, filterValues);
  }, [pageNumber, pageSize]);

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1); // Reset to the first page
  };

  // Handle page number change
  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };

  const fetchInvoiceStatus = async () => {
    try {
      const response: ApiResponse<any> = await apiRequest({
        url: `/receivableinvoice/InvoiceStatusList`,
        method: "GET",
      });
      if (response.success && response.data) {
        onSuccess?.(
          "OPTIONS",
          "fetchInvoiceStatus",
          response?.message || "Data retrieved successfully!"
        );
        const options = response?.data?.map((status: InvoiceStatus) => ({
          label: status.codeName,
          value: status.masterId.toString(),
        }));
        setCustomerOptions(options || []);
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching Invoice Status."));
    }
  };

  useEffect(() => {
    fetchInvoiceStatus();
  }, []);

  return (
    <InvoiceContext.Provider
      value={{
        invoices,
        isLoading,
        handleApplyFilter,
        handleClearFilter,
        pageNumber,
        pageSize,
        totalPages,
        totalRecords,
        handlePageChange,
        handlePageSizeChange,
        customerOptions,
      }}
    >
      {children}
    </InvoiceContext.Provider>
  );
};
